import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { getCurrentUser } from 'aws-amplify/auth';
import { 
  MessageSquare, User, Calendar, Plus, 
  ArrowLeft, Eye
} from 'lucide-react';
import './Tavern.css';

// Mock data - will be replaced with actual data from the API
const MOCK_CATEGORIES = [
  {
    id: '1',
    name: 'Announcements',
    description: 'Official announcements from the ChoiceRealms team',
    threadCount: 5,
    postCount: 12
  },
  {
    id: '2',
    name: 'General Discussion',
    description: 'Talk about anything related to ChoiceRealms',
    threadCount: 24,
    postCount: 189
  },
  {
    id: '3',
    name: 'Character Builds',
    description: 'Share and discuss character builds and strategies',
    threadCount: 18,
    postCount: 142
  },
  {
    id: '4',
    name: 'Game Modules',
    description: 'Discuss official and community game modules',
    threadCount: 15,
    postCount: 87
  },
  {
    id: '5',
    name: 'Technical Support',
    description: 'Get help with technical issues',
    threadCount: 8,
    postCount: 36
  }
];

const MOCK_THREADS = {
  '1': [
    {
      id: '101',
      title: 'Welcome to ChoiceRealms',
      content: 'Welcome to the official forums for ChoiceRealms! This is where you can discuss all aspects of the game, share your experiences, and connect with other players.',
      author: 'GameMaster',
      createdAt: '2023-09-01T10:00:00Z',
      lastActivity: '2023-09-15T14:30:00Z',
      lastPostAuthor: 'GameMaster',
      viewCount: 352,
      replyCount: 7,
      isPinned: true,
      isLocked: false
    },
    {
      id: '102',
      title: 'Latest Updates and Upcoming Features',
      content: 'Check out our latest updates and a sneak peek at upcoming features!',
      author: 'GameMaster',
      createdAt: '2023-09-05T14:00:00Z',
      lastActivity: '2023-09-10T10:15:00Z',
      lastPostAuthor: 'ExcitedPlayer',
      viewCount: 245,
      replyCount: 4,
      isPinned: true,
      isLocked: false
    }
  ],
  '2': [
    {
      id: '201',
      title: 'What class is everyone playing?',
      content: 'I\'m curious to know what classes people are choosing. I started with a Paladin but thinking about making a Wizard next.',
      author: 'AdventurerJohn',
      createdAt: '2023-09-10T09:45:00Z',
      lastActivity: '2023-09-14T09:45:00Z',
      lastPostAuthor: 'MageSupreme',
      viewCount: 128,
      replyCount: 22,
      isPinned: false,
      isLocked: false
    },
    {
      id: '202',
      title: 'Looking for party members',
      content: 'Anyone interested in forming a party for the Wintermere campaign? I\'m a level 5 Warrior looking for a healer and some DPS.',
      author: 'DungeonCrawler',
      createdAt: '2023-09-12T15:30:00Z',
      lastActivity: '2023-09-13T22:30:00Z',
      lastPostAuthor: 'HealingPriest',
      viewCount: 87,
      replyCount: 8,
      isPinned: false,
      isLocked: false
    },
    {
      id: '203',
      title: 'First impressions of ChoiceRealms',
      content: 'Just started playing yesterday and wanted to share my first impressions. So far, I\'m really enjoying the depth of the character creation and the writing quality!',
      author: 'NewAdventurer',
      createdAt: '2023-09-08T20:15:00Z',
      lastActivity: '2023-09-09T14:20:00Z',
      lastPostAuthor: 'VeteranPlayer',
      viewCount: 143,
      replyCount: 12,
      isPinned: false,
      isLocked: false
    }
  ],
  '3': [
    {
      id: '301',
      title: 'Ultimate Wizard Guide',
      content: 'I\'ve been playing a Wizard for the past month and wanted to share my build and strategies that have worked really well.',
      author: 'SpellcasterSupreme',
      createdAt: '2023-09-07T17:20:00Z',
      lastActivity: '2023-09-12T17:20:00Z',
      lastPostAuthor: 'ApprenticeWizard',
      viewCount: 275,
      replyCount: 34,
      isPinned: true,
      isLocked: false
    },
    {
      id: '302',
      title: 'Tank Builds for Wintermere',
      content: 'What are the best tank builds for the Wintermere campaign? I\'m finding the final boss particularly challenging.',
      author: 'ShieldWall',
      createdAt: '2023-09-06T13:10:00Z',
      lastActivity: '2023-09-11T13:10:00Z',
      lastPostAuthor: 'DefensiveTactics',
      viewCount: 189,
      replyCount: 17,
      isPinned: false,
      isLocked: false
    }
  ],
  '4': [
    {
      id: '401',
      title: 'Wintermere Feedback Thread',
      content: 'Share your thoughts on the Wintermere module. What did you like? What could be improved?',
      author: 'IcyExplorer',
      createdAt: '2023-09-04T11:05:00Z',
      lastActivity: '2023-09-13T11:05:00Z',
      lastPostAuthor: 'GameMaster',
      viewCount: 316,
      replyCount: 42,
      isPinned: true,
      isLocked: false
    },
    {
      id: '402',
      title: 'Dark Forest Module Guide',
      content: 'A complete walkthrough of the Dark Forest module with tips for surviving each encounter.',
      author: 'ForestWanderer',
      createdAt: '2023-09-03T19:40:00Z',
      lastActivity: '2023-09-09T19:40:00Z',
      lastPostAuthor: 'LostAdventurer',
      viewCount: 412,
      replyCount: 29,
      isPinned: false,
      isLocked: false
    }
  ],
  '5': [
    {
      id: '501',
      title: 'Character not loading properly',
      content: 'My character doesn\'t load properly after the latest update. Anyone else experiencing this issue?',
      author: 'TroubledPlayer',
      createdAt: '2023-09-14T16:55:00Z',
      lastActivity: '2023-09-14T16:55:00Z',
      lastPostAuthor: 'TroubledPlayer',
      viewCount: 24,
      replyCount: 1,
      isPinned: false,
      isLocked: false
    },
    {
      id: '502',
      title: 'Login Issues After Update',
      content: 'I\'m unable to log in after the latest update. I\'ve tried clearing my cache and cookies, but still no luck.',
      author: 'HelpMePlease',
      createdAt: '2023-09-10T08:30:00Z',
      lastActivity: '2023-09-10T08:30:00Z',
      lastPostAuthor: 'TechSupport',
      viewCount: 98,
      replyCount: 7,
      isPinned: false,
      isLocked: true
    }
  ]
};

const formatDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const TavernCategory = () => {
  const { categoryId } = useParams();
  const navigate = useNavigate();
  
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const threadsPerPage = 10;

  useEffect(() => {
    checkUser();
  }, []);

  async function checkUser() {
    try {
      const authenticatedUser = await getCurrentUser();
      setUser(authenticatedUser);
    } catch (err) {
      console.log('Error getting current user:', err);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  const category = MOCK_CATEGORIES.find(c => c.id === categoryId);
  if (!category) {
    return (
      <div className="tavern-container">
        <div className="thread-detail">
          <div className="thread-detail__header">
            <h1 className="thread-detail__title">Category Not Found</h1>
          </div>
          <div className="thread-detail__content">
            <p>The category you're looking for doesn't exist or has been removed.</p>
            <div className="tavern-actions" style={{ marginTop: '1rem' }}>
              <button 
                className="tavern-action-button"
                onClick={() => navigate('/tavern')}
              >
                Back to Tavern
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const threads = MOCK_THREADS[categoryId] || [];
  
  // Sort threads: pinned first, then by lastActivity date
  const sortedThreads = [...threads].sort((a, b) => {
    if (a.isPinned && !b.isPinned) return -1;
    if (!a.isPinned && b.isPinned) return 1;
    return new Date(b.lastActivity) - new Date(a.lastActivity);
  });

  // Pagination
  const indexOfLastThread = currentPage * threadsPerPage;
  const indexOfFirstThread = indexOfLastThread - threadsPerPage;
  const currentThreads = sortedThreads.slice(indexOfFirstThread, indexOfLastThread);
  const totalPages = Math.ceil(sortedThreads.length / threadsPerPage);

  const handleNewThread = () => {
    navigate(`/tavern/category/${categoryId}/new-thread`);
  };

  const handleThreadSelect = (threadId) => {
    navigate(`/tavern/thread/${threadId}`);
  };

  return (
    <div className="tavern-container">
      <header className="tavern-header">
        <div className="tavern-header__content">
          <h1 className="tavern-title">{category.name}</h1>
          <p className="tavern-description">{category.description}</p>
          <div className="category-card__stats" style={{ justifyContent: 'center', marginTop: 'var(--spacing-sm)' }}>
            <div className="category-card__stat">
              <MessageSquare size={16} />
              {category.threadCount} Threads
            </div>
            <div className="category-card__stat">
              <MessageSquare size={16} />
              {category.postCount} Posts
            </div>
          </div>
        </div>
      </header>

      <main>
        <div className="thread-list-header">
          <h2 className="thread-list-title">Threads</h2>
          <button className="tavern-action-button" onClick={handleNewThread}>
            <Plus size={16} />
            New Thread
          </button>
        </div>
        
        <div className="thread-list">
          {currentThreads.length === 0 ? (
            <div className="empty-state" style={{ textAlign: 'center', padding: 'var(--spacing-lg)' }}>
              <p>No threads have been created in this category yet.</p>
              <p>Be the first to start a discussion!</p>
              <button 
                className="tavern-action-button" 
                onClick={handleNewThread}
                style={{ marginTop: 'var(--spacing-md)' }}
              >
                <Plus size={16} />
                New Thread
              </button>
            </div>
          ) : (
            currentThreads.map(thread => (
              <div 
                key={thread.id} 
                className={`thread-card ${thread.isPinned ? 'thread-card--pinned' : ''} ${thread.isLocked ? 'thread-card--locked' : ''}`}
                onClick={() => handleThreadSelect(thread.id)}
              >
                <div className="thread-card__main">
                  <Link 
                    to={`/tavern/thread/${thread.id}`} 
                    className="thread-card__title"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {thread.title}
                  </Link>
                  <div className="thread-card__meta">
                    <div className="thread-card__author">
                      <User size={14} />
                      {thread.author}
                    </div>
                    <div>
                      <Calendar size={14} />
                      {formatDate(thread.createdAt)}
                    </div>
                  </div>
                  <p className="thread-card__excerpt">{thread.content}</p>
                </div>
                <div className="thread-card__stats">
                  <div className="thread-card__stat">
                    <div className="thread-card__stat-value">{thread.viewCount}</div>
                    <div className="thread-card__stat-label">Views</div>
                  </div>
                  <div className="thread-card__stat">
                    <div className="thread-card__stat-value">{thread.replyCount}</div>
                    <div className="thread-card__stat-label">Replies</div>
                  </div>
                </div>
                <div className="thread-card__last-activity">
                  <div className="thread-card__time">{formatDate(thread.lastActivity)}</div>
                  <div className="thread-card__user">by {thread.lastPostAuthor}</div>
                </div>
              </div>
            ))
          )}
        </div>
        
        {totalPages > 1 && (
          <div className="pagination">
            <button 
              className={`pagination__item pagination__item--prev ${currentPage === 1 ? 'pagination__item--disabled' : ''}`}
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
            >
              «
            </button>
            
            {[...Array(totalPages)].map((_, index) => (
              <button
                key={index}
                className={`pagination__item ${currentPage === index + 1 ? 'pagination__item--active' : ''}`}
                onClick={() => setCurrentPage(index + 1)}
              >
                {index + 1}
              </button>
            ))}
            
            <button 
              className={`pagination__item pagination__item--next ${currentPage === totalPages ? 'pagination__item--disabled' : ''}`}
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
            >
              »
            </button>
          </div>
        )}
        
        <div className="tavern-actions" style={{ marginTop: '2rem', justifyContent: 'space-between' }}>
          <button 
            className="tavern-action-button"
            onClick={() => navigate('/tavern')}
          >
            <ArrowLeft size={16} />
            Back to Categories
          </button>
          <button 
            className="tavern-action-button"
            onClick={handleNewThread}
          >
            <Plus size={16} />
            New Thread
          </button>
        </div>
      </main>
    </div>
  );
};

export default TavernCategory;